import { useCallback } from "react";
import type { ViewType } from "./types.ts";
import { useCalendarEvents } from "./useCalendarEvents.ts";
import { useCalendarSelection } from "./useCalendarSelection.ts";
import { useTimeRangeEnrichment } from "./useTimeRangeEnrichment.tsx";

interface UseCalendarSurfaceArgs {
  /** Currently displayed date. Controlled by the wrapper. */
  date: Date;
  /** Currently displayed view type. Controlled by the wrapper. */
  viewType: ViewType;
  /**
   * Optional filter: only fetch events on these calendar IDs. Pass `null` to
   * indicate "calendar list is still loading" (skips the network call). Omit
   * or pass `undefined` to fetch all visible calendars.
   */
  calendarIds?: string[] | null;
}

/**
 * Shared wiring for a calendar surface — the canvas calendar on home,
 * `/calendar`, and the thread canvas.
 *
 * Encapsulates the three concerns every surface needs identically:
 *   1. Event fetching via `useCalendarEvents`
 *   2. Selection state via `useCalendarSelection` (shared across surfaces)
 *   3. Time-range enrichment registration via `useTimeRangeEnrichment`
 *
 * Plus the per-surface grid-prop bundle (`gridProps`) that would otherwise be
 * duplicated verbatim across `<CalendarView>` call sites.
 *
 * What stays with the wrapper:
 *   - Header chrome (prev/next/today, title, view picker) — differs between
 *     surfaces today; unifying them is a separate slice.
 *   - Frame (ViewWrapper / overall layout)
 *   - Anything that reads `events` or `selection` for its own rendering
 *     (e.g. the page's omnibox focus logic reads `selection.ranges.length`).
 *     The hook exposes those directly on its return for that reason.
 */
export function useCalendarSurface({ date, viewType, calendarIds }: UseCalendarSurfaceArgs) {
  const { events, loadedRange } = useCalendarEvents(date, viewType, calendarIds);
  const selection = useCalendarSelection(events, loadedRange);
  useTimeRangeEnrichment(selection);

  // Taps on a selection bg-event remove that range. The bg-event has
  // `pointer-events: auto` in CSS and sits above event chips via z-index, so
  // this fires regardless of what calendar event happens to sit underneath.
  const { removeRange } = selection;
  const handleRawEventClick = useCallback(
    (info: { event: { id: string; extendedProps: Record<string, unknown> } }) => {
      if (info.event.extendedProps["isTimeRangeSelection"]) {
        removeRange(info.event.id);
      }
    },
    [removeRange],
  );

  return {
    events,
    selection,
    /**
     * Spread into `<CalendarView {...gridProps} />` to wire selection into
     * the grid. Covers `selectable`, `onSelect`, `onDateClick`,
     * `additionalEvents`, `slotDuration`, `onResizeRange` (mobile iOS-style
     * resize handles), and `onRawEventClick` (tap-to-remove on the range
     * bg-event itself). The caller still supplies the non-selection props
     * (viewType, currentDate, onDateChange, etc.).
     */
    gridProps: {
      selectable: true,
      onSelect: selection.handleSelect,
      additionalEvents: selection.additionalEvents,
      onDateClick: selection.handleDateClick,
      onResizeRange: selection.resizeRange,
      onRawEventClick: handleRawEventClick,
      slotDuration: "00:30:00" as const,
    },
  };
}
